import { randomUuid } from "@models/utils/uuid";
import { Ioc } from "@core/ioc/ioc";
import type {
  MediaAssetData,
  MediaTileData,
  MediaTilePatch,
  MediaTileUpdateFlag,
} from "@models/data/mediaTileData";
import {
  createDefaultMediaAsset,
  highlightJsonOrNull,
  mapMediaAssetDtoToData,
  mapWireMediaTile,
  mediaAssetDataToDto,
  mediaTileDataToDto,
  mediaTileFlagMask,
  MEDIA_ASSET_TYPE,
  MEDIA_TILE_ANCHOR_POINT,
  MEDIA_TILE_DEFAULT_BACKGROUND,
  MEDIA_TILE_DEFAULT_BILLBOARD,
  MEDIA_TILE_DEFAULT_SCALE,
  MEDIA_TILE_MIN_WIDTH,
  MEDIA_TILE_PLACEMENT,
  nextMediaTileName,
} from "@models/data/mediaTileData";
import { toNameFloatMap, vec3From } from "@models/data/nameFloatMap";
import type { Vec3 } from "@models/data/entityData";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { SELECTION_MODEL } from "@models/scene/ISelectionModel";
import { UNDO_REDO, type IUndoRedo } from "./IUndoRedo";
import {
  ORBIT_CAMERA,
  type OrbitCameraController,
} from "@runtime/camera/orbitCameraController";
import { threeCameraQuaternionToUnityEuler } from "@runtime/assets/mediaTileTransform";
import { EngineService, ENGINE_SERVICE } from "@runtime/engine/engineService";
import { worldToStageQuaternion } from "@runtime/xr/stageSpace";
import { Object3D, Quaternion } from "three";
import type { ProjectModel } from "./ProjectModel";

/**
 * `ProjectModelMediaTiles` — partial of {@link ProjectModel} owning
 * cards ("Add card") and action-button media tiles. Port of
 * `Models/ProjectModel/ProjectModel_MediaTiles.cs`.
 *
 * Media tiles are **not** entity snapshots: each is a per-scene
 * `mediatile` Dynamo row (transform + presentation) joined at load time
 * to a per-project `mediaasset` row (text / image content) — see
 * `mediaTileData.ts` for the row shapes. Creation mirrors
 * `AssetFactory.CreateTextBox` → `CreateMediaTile`; edits mirror
 * `UpdateMediaTile(s)` (full-row put) and `UpdateMediaAssetText/Image`;
 * realtime events reuse Unity's `create_mediatile` /
 * `update_mediatile` / `delete_mediatile` / `update_mediaasset` types.
 */
export class ProjectModelMediaTiles {
  hasMediaTiles(this: ProjectModel, sceneId: string): boolean {
    return this.getMediaTiles(sceneId).length > 0;
  }

  getMediaTiles(this: ProjectModel, sceneId: string): ReadonlyArray<MediaTileData> {
    return internals(this).m_mediaTilesByScene.get(sceneId) ?? [];
  }

  getMediaTile(this: ProjectModel, sceneId: string, tileId: string): MediaTileData | null {
    return this.getMediaTiles(sceneId).find((tile) => tile.id === tileId) ?? null;
  }

  /**
   * Port of `AssetFactory.CreateTextBox` + `ProjectModel.CreateMediaTile`
   * (`ProjectModel_MediaTiles.cs:25`): empty text asset, `"Card N"` name,
   * anchor-point position, camera-facing rotation, and current card defaults.
   * The new tile is selected on completion, matching Unity's `SelectObjects`.
   */
  async createTextTileAsync(
    this: ProjectModel,
    signal?: AbortSignal,
  ): Promise<MediaTileData | null> {
    const scene = this.activeScene;
    if (!this.activeProject || !scene) return null;

    const asset: MediaAssetData = createDefaultMediaAsset(randomUuid());
    const tile: MediaTileData = {
      id: randomUuid(),
      assetId: asset.id,
      name: nextMediaTileName(this.getMediaTiles(scene.id), asset.mediaType),
      position: MEDIA_TILE_ANCHOR_POINT,
      rotationEuler: defaultMediaTileRotation(),
      scale: MEDIA_TILE_DEFAULT_SCALE,
      visibility: true,
      isLocked: false,
      forcedVisibility: false,
      billboard: MEDIA_TILE_DEFAULT_BILLBOARD,
      width: MEDIA_TILE_MIN_WIDTH,
      fixedWidth: false,
      backgroundColor: MEDIA_TILE_DEFAULT_BACKGROUND,
      imagePlacement: MEDIA_TILE_PLACEMENT.Bottom,
      persistentHighlightJson: null,
      asset,
    };

    await createMediaTileAsync(this, scene.id, tile, signal);
    return tile;
  }

  /**
   * Port of `ProjectModel.DuplicateMediaTile` (`ProjectModel_MediaTiles.cs:39`)
   * — Ctrl+D, the context menu's Duplicate, and Paste. Unity copies everything
   * but the two ids and `FixedWidth` (`MediaTileData.CopyTo` skips it, so a copy
   * grows with its text again): the name and the pose are kept as they are, so
   * the copy sits exactly on top of its source, and the image is SHARED by
   * `ImageId` rather than re-uploaded (which is why a delete only removes a
   * texture no other asset still uses). `targetSceneId` is the scene a Paste
   * lands in; the copy is selected, as every Unity create is.
   */
  async duplicateMediaTileAsync(
    this: ProjectModel,
    sourceSceneId: string,
    tileId: string,
    targetSceneId: string = sourceSceneId,
    signal?: AbortSignal,
  ): Promise<MediaTileData | null> {
    if (!this.activeProject || !this.getScene(targetSceneId)) return null;
    const source = this.getMediaTile(sourceSceneId, tileId);
    if (!source) return null;
    const asset: MediaAssetData = { ...source.asset, id: randomUuid() };
    const tile: MediaTileData = {
      ...source,
      id: randomUuid(),
      assetId: asset.id,
      asset,
      fixedWidth: false,
    };
    await createMediaTileAsync(this, targetSceneId, tile, signal);
    return tile;
  }

  /**
   * {@link updateMediaTileAsync}, recorded on the undo stack — the card edits
   * Unity can undo: a handle drag (Battlehub records its own), the Placement
   * page's `BeginRecordValue` / `EndRecordValue` on `LocalPosition` /
   * `LocalEuler` / `LocalScale`, surface placement, and a rename
   * (`SelectedGameObjectModel.cs:1541-1607`). Billboard, width, background,
   * image and lock are NOT undoable upstream and do not come through here.
   *
   * `before` defaults to the card's current values for the keys `patch`
   * carries. A caller that has already moved the card in memory — a surface
   * placement preview — passes the pose it started from instead.
   */
  async updateMediaTileWithUndoAsync(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    patch: MediaTilePatch,
    flags: ReadonlyArray<MediaTileUpdateFlag>,
    options: { readonly label?: string; readonly before?: MediaTilePatch } = {},
  ): Promise<void> {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current) return;
    const before = options.before ?? pickTilePatch(current, patch);
    await this.updateMediaTileAsync(sceneId, tileId, patch, flags);
    recordUndo(options.label ?? "Edit card", {
      redo: () => this.updateMediaTileAsync(sceneId, tileId, patch, flags),
      undo: () => this.updateMediaTileAsync(sceneId, tileId, before, flags),
    });
  }

  /**
   * {@link updateMediaAssetTextAsync}, recorded on the undo stack. Unity opens
   * a record when an edit begins and closes it at end-edit — which its editor
   * raises every 2 s while typing — so one undo step is one committed chunk of
   * text (`MediaTileContextViewModel.OnTextBeginEdit` / `OnTextEndEdit`).
   */
  async updateMediaAssetTextWithUndoAsync(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    text: string,
    previousText: string,
  ): Promise<void> {
    if (text === previousText) return;
    await this.updateMediaAssetTextAsync(sceneId, tileId, text);
    recordUndo("Edit card text", {
      redo: () => this.updateMediaAssetTextAsync(sceneId, tileId, text),
      undo: () => this.updateMediaAssetTextAsync(sceneId, tileId, previousText),
    });
  }

  /**
   * Port of `ProjectModel.UpdateMediaTile` / `UpdateMediaTilesAsync`:
   * apply the patch locally, persist the full row (Unity always writes a
   * replace put), and publish `update_mediatile` with the changed flags.
   */
  async updateMediaTileAsync(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    patch: MediaTilePatch,
    flags: ReadonlyArray<MediaTileUpdateFlag>,
    signal?: AbortSignal,
  ): Promise<void> {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current) return;
    const next = patchTile(current, patch);
    replaceTile(this, sceneId, next);
    this.onMediaTileUpdated.emit({ sceneId, tileId });
    if (this.isWorkflowPlaybackMutationScopeActive) return;

    const context = storageContext(this);
    if (context) {
      try {
        await context.storage.updateMediaTiles(
          context.customerId,
          context.projectId,
          sceneId,
          [mediaTileDataToDto(next)],
          signal,
        );
      } catch (err) {
        // Roll the EDIT back, not the card: a highlight applied while the
        // write was in flight is runtime state the write never carried.
        const latest = this.getMediaTile(sceneId, tileId);
        replaceTile(this, sceneId, {
          ...current,
          persistentHighlightJson: latest?.persistentHighlightJson ?? current.persistentHighlightJson,
        });
        this.onMediaTileUpdated.emit({ sceneId, tileId });
        throw err;
      }
    }

    publishMediaTileEvent(this, "update_mediatile", sceneId, {
      mtids: [tileId],
      MediaTileIDs: [tileId],
      data: wireBulkData(next, flags),
      Data: wireBulkData(next, flags),
      updateEntityFlags: mediaTileFlagMask(flags),
      UpdateEntityFlags: mediaTileFlagMask(flags),
    });
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  /**
   * In-memory-only transform preview for the surface-placement drag
   * (`ISurfacePlacementModel`): mutate the tile's Unity-space position /
   * rotation and emit `onMediaTileUpdated` so `MediaTileRenderer`
   * repositions, without persisting to storage or publishing a realtime
   * event. Commit persists through {@link updateMediaTileAsync}; cancel
   * restores the initial transform through this same path.
   */
  previewMediaTileTransform(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    position: Vec3,
    rotationEuler: Vec3,
  ): void {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current) return;
    replaceTile(this, sceneId, { ...current, position, rotationEuler });
    this.onMediaTileUpdated.emit({ sceneId, tileId });
  }

  /**
   * In-memory-only presentation preview — the inspector's auto-width while
   * typing, which Unity shows at once and saves with the next text commit
   * (`MediaTileContextViewModel.SetPreferredMediaWidth`). No storage write and
   * no publish; commit through {@link updateMediaTileAsync}.
   */
  previewMediaTilePatch(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    patch: MediaTilePatch,
  ): void {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current) return;
    replaceTile(this, sceneId, patchTile(current, patch));
    this.onMediaTileUpdated.emit({ sceneId, tileId });
  }

  /**
   * Set (`string`) or clear (`null`) the persistent highlight of one or more
   * cards — the card half of `highlight_part` / `clear_highlight`, a run's
   * revert, and a finite pulse's auto-clear. Unity's
   * `IPersistentEntityHighlightModel.SetHighlight` / `ClearHighlight` on a
   * media tile.
   *
   * RUNTIME-ONLY, like a part's highlight (`snapshotPatchForPersistence`): no
   * row write and no `update_mediatile`. That is upstream's own behaviour since
   * `09f21fa45` (2026-09-22) took Spark's highlight persistence out, and
   * Unity's `ToMediaTileItem` never wrote the attribute in the first place.
   *
   * Every card changes before any is announced, so a group starts its pulse on
   * one frame — the same reason `updateEntitySnapshotsAsync` batches.
   */
  applyMediaTileHighlights(
    this: ProjectModel,
    sceneId: string,
    updates: ReadonlyArray<{
      readonly tileId: string;
      readonly persistentHighlightJson: string | null;
    }>,
  ): void {
    const changed: string[] = [];
    for (const update of updates) {
      const current = this.getMediaTile(sceneId, update.tileId);
      if (!current || current.persistentHighlightJson === update.persistentHighlightJson) continue;
      replaceTile(this, sceneId, {
        ...current,
        persistentHighlightJson: update.persistentHighlightJson,
      });
      changed.push(update.tileId);
    }
    for (const tileId of changed) this.onMediaTileUpdated.emit({ sceneId, tileId });
  }

  /**
   * In-memory-only text preview: the card redraws on every keystroke, as
   * Unity's `MediaText` setter refreshes the tile without a write. The text
   * the row last persisted is remembered, so the commit that follows
   * ({@link updateMediaAssetTextAsync}) still sees a change to save.
   */
  previewMediaAssetText(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    text: string,
  ): void {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current || current.asset.text === text) return;
    const state = internals(this);
    if (!state.m_mediaAssetPersistedText.has(current.asset.id)) {
      state.m_mediaAssetPersistedText.set(current.asset.id, current.asset.text);
    }
    const nextAsset: MediaAssetData = { ...current.asset, text };
    state.m_mediaAssetsById.set(nextAsset.id, nextAsset);
    replaceTile(this, sceneId, { ...current, asset: nextAsset });
    this.onMediaTileUpdated.emit({ sceneId, tileId });
  }

  /**
   * Port of `ProjectModel.UpdateMediaAssetText`. Compares against what the row
   * holds — the pre-preview text when {@link previewMediaAssetText} has run —
   * so a previewed edit is still written, and an unchanged one is not.
   */
  async updateMediaAssetTextAsync(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current) return;
    const state = internals(this);
    const persistedText = state.m_mediaAssetPersistedText.get(current.asset.id) ?? current.asset.text;
    state.m_mediaAssetPersistedText.delete(current.asset.id);
    const nextAsset: MediaAssetData = { ...current.asset, text };
    if (current.asset.text !== text) {
      state.m_mediaAssetsById.set(nextAsset.id, nextAsset);
      replaceTile(this, sceneId, { ...current, asset: nextAsset });
      this.onMediaTileUpdated.emit({ sceneId, tileId });
    }
    if (persistedText === text) return;

    const context = storageContext(this);
    if (context) {
      try {
        await context.storage.updateMediaAssetText(
          context.customerId,
          context.projectId,
          nextAsset.id,
          text,
          nextAsset.actionDataId,
          signal,
        );
      } catch (err) {
        // Back to what the row really holds; the editor keeps what was typed.
        const persistedAsset: MediaAssetData = { ...current.asset, text: persistedText };
        state.m_mediaAssetsById.set(persistedAsset.id, persistedAsset);
        const latest = this.getMediaTile(sceneId, tileId) ?? current;
        replaceTile(this, sceneId, { ...latest, asset: persistedAsset });
        this.onMediaTileUpdated.emit({ sceneId, tileId });
        throw err;
      }
    }

    publishMediaTileEvent(this, "update_mediaasset", sceneId, {
      mtid: tileId,
      MediaTileId: tileId,
    });
    // Unity `MediaTileContextViewModel.OnMediaTextChanged` refreshes the
    // active scene card after the media-asset write: card text is part of the
    // rendered scene, even though it lives on the `mediaasset` row rather than
    // the `mediatile` row.
    this.scheduleActiveSceneThumbnail();
  }

  /**
   * Port of `ProjectModel.UpdateMediaAssetImage`: upload the new texture
   * to `textures/{textureId}` (new UUID), delete the previous one when no
   * other asset references it, update the asset row's `ImageId`, and
   * publish `update_mediaasset` (peers re-fetch the asset + image).
   * Passing `null` clears the image.
   */
  async updateMediaAssetImageAsync(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    file: File | null,
    signal?: AbortSignal,
  ): Promise<void> {
    const current = this.getMediaTile(sceneId, tileId);
    if (
      !current ||
      current.asset.mediaType === MEDIA_ASSET_TYPE.ActionButton
    ) {
      return;
    }
    const state = internals(this);
    const context = storageContext(this);
    const previousImageId = current.asset.imageId;

    let imageId = "";
    if (file) {
      imageId = randomUuid();
      if (context) {
        await context.storage.putTextureObject(context.customerId, imageId, file, signal);
      }
    }

    const nextAsset: MediaAssetData = {
      ...current.asset,
      imageId,
      modifiedAt: new Date().toISOString(),
    };
    state.m_mediaAssetsById.set(nextAsset.id, nextAsset);
    replaceTile(this, sceneId, { ...current, asset: nextAsset });

    // Local URL cache: the picked file doubles as the preview/render source.
    evictImageUrl(this, previousImageId);
    if (file && imageId) {
      state.m_mediaTileImageUrlById.set(imageId, URL.createObjectURL(file));
    }
    this.onMediaTileUpdated.emit({ sceneId, tileId });

    if (context) {
      await context.storage.updateMediaAssetImage(
        context.customerId,
        context.projectId,
        nextAsset.id,
        imageId,
        signal,
      );
      // Mirrors `DeleteMediaTileTexture`: remove the old object only when
      // no other media asset still references it.
      if (previousImageId && !isImageInUse(this, previousImageId)) {
        await context.storage
          .deleteTextureObject(context.customerId, previousImageId, signal)
          .catch((err: unknown) => {
            console.warn("[ProjectModel] previous media texture delete failed", err);
          });
      }
    }

    publishMediaTileEvent(this, "update_mediaasset", sceneId, {
      mtid: tileId,
      MediaTileId: tileId,
    });
    // The image belongs to the media asset row, but it visibly changes the
    // scene card just like tile transform/style edits do.
    this.scheduleActiveSceneThumbnail();
  }

  /** Port of `ProjectModel.DeleteMediaTile`. */
  async deleteMediaTileAsync(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const tile = this.getMediaTile(sceneId, tileId);
    if (!tile) return;
    const state = internals(this);

    removeTile(this, sceneId, tileId);
    state.m_mediaAssetsById.delete(tile.assetId);

    const context = storageContext(this);
    if (context) {
      await context.storage.deleteMediaTiles(
        context.customerId,
        context.projectId,
        sceneId,
        [tileId],
        signal,
      );
      // Unity intends to delete the asset row too (its `DeleteMediaAssets`
      // key bug orphans them upstream); we delete the canonical key.
      await context.storage
        .deleteMediaAssets(context.customerId, context.projectId, [tile.assetId], signal)
        .catch((err: unknown) => {
          console.warn("[ProjectModel] media asset delete failed", err);
        });
      await removeSceneMediaAssetId(this, context, sceneId, tile.assetId, signal);
      if (tile.asset.imageId && !isImageInUse(this, tile.asset.imageId)) {
        await context.storage
          .deleteTextureObject(context.customerId, tile.asset.imageId, signal)
          .catch((err: unknown) => {
            console.warn("[ProjectModel] media texture delete failed", err);
          });
      }
    }
    evictImageUrl(this, tile.asset.imageId);

    publishMediaTileEvent(this, "delete_mediatile", sceneId, {
      mtid: tileId,
      MediaTileId: tileId,
    });

    if (Ioc.isRegistered<ISelectionModel>(SELECTION_MODEL)) {
      const selection = Ioc.resolve<ISelectionModel>(SELECTION_MODEL);
      if (selection.isSelected(tileId)) selection.clear();
    }
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  getMediaTileImageUrl(this: ProjectModel, imageId: string): string | null {
    if (!imageId) return null;
    return internals(this).m_mediaTileImageUrlById.get(imageId) ?? null;
  }

  /** Download + cache a media-tile image (`textures/{imageId}` in S3). */
  async loadMediaTileImageUrlAsync(
    this: ProjectModel,
    imageId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!imageId) return null;
    const state = internals(this);
    const cached = state.m_mediaTileImageUrlById.get(imageId);
    if (cached) return cached;
    const context = storageContext(this);
    if (!context) return null;
    const url = await context.storage.getTextureBlobUrl(context.customerId, imageId, signal);
    if (!url) return null;
    state.m_mediaTileImageUrlById.set(imageId, url);
    return url;
  }

  // -- Server → client apply paths (realtime reconciliation) --

  /** Port of `ProjectModel.ApplyCreateMediaTile` (no storage write, no event). */
  async applyCreateMediaTile(
    this: ProjectModel,
    sceneId: string,
    wireTile: Record<string, unknown>,
  ): Promise<void> {
    const state = internals(this);
    const assetId = stringOf(wireTile, "assetId", "AssetId", "AssetID");
    let asset = assetId ? state.m_mediaAssetsById.get(assetId) ?? null : null;
    if (!asset && assetId) {
      // Unity `RefreshMediaAssetAsync`: fetch the asset row the creator wrote.
      const context = storageContext(this);
      if (context) {
        const dto = await context.storage.getMediaAsset(
          context.customerId,
          context.projectId,
          assetId,
        );
        if (dto) asset = mapMediaAssetDtoToData(dto);
      }
    }
    if (!asset) asset = createDefaultMediaAsset(assetId ?? randomUuid());

    const tile = mapWireMediaTile(wireTile, asset);
    if (!tile) return;
    if (this.getMediaTile(sceneId, tile.id)) return;
    state.m_mediaAssetsById.set(asset.id, asset);
    insertTile(this, sceneId, tile);
  }

  /** Port of `ProjectModel.ApplyDeleteMediaTile`. */
  applyDeleteMediaTile(this: ProjectModel, sceneId: string, tileId: string): void {
    const tile = this.getMediaTile(sceneId, tileId);
    if (!tile) return;
    removeTile(this, sceneId, tileId);
    internals(this).m_mediaAssetsById.delete(tile.assetId);
  }

  /** Port of `ProjectModel.ApplyMediaTileChanges` (BulkMediaTileData + flags). */
  applyMediaTileChanges(
    this: ProjectModel,
    sceneId: string,
    tileIds: ReadonlyArray<string>,
    wireData: Record<string, unknown>,
    flagMask: number,
  ): void {
    for (const tileId of tileIds) {
      const current = this.getMediaTile(sceneId, tileId);
      if (!current) continue;
      const next = applyWireBulkData(current, wireData, flagMask);
      replaceTile(this, sceneId, next);
      this.onMediaTileUpdated.emit({ sceneId, tileId });
    }
  }

  /** Port of `ProjectModel.ApplyMediaAssetChange` — re-fetch asset + image. */
  async applyMediaAssetChange(
    this: ProjectModel,
    sceneId: string,
    tileId: string,
  ): Promise<void> {
    const current = this.getMediaTile(sceneId, tileId);
    if (!current) return;
    const context = storageContext(this);
    if (!context) return;
    const dto = await context.storage.getMediaAsset(
      context.customerId,
      context.projectId,
      current.assetId,
    );
    if (!dto) return;
    const state = internals(this);
    const previousImageId = current.asset.imageId;
    const nextAsset = mapMediaAssetDtoToData(dto);
    // A peer's text replaces any local preview, and is what the row now holds.
    state.m_mediaAssetPersistedText.delete(nextAsset.id);
    state.m_mediaAssetsById.set(nextAsset.id, nextAsset);
    replaceTile(this, sceneId, { ...current, asset: nextAsset });
    if (previousImageId !== nextAsset.imageId) evictImageUrl(this, previousImageId);
    this.onMediaTileUpdated.emit({ sceneId, tileId });
  }
}

// Helpers --------------------------------------------------------------------

interface MediaTileInternals {
  readonly m_mediaTilesByScene: Map<string, MediaTileData[]>;
  readonly m_mediaAssetsById: Map<string, MediaAssetData>;
  readonly m_mediaTileImageUrlById: Map<string, string>;
  readonly m_mediaAssetPersistedText: Map<string, string>;
}

function internals(model: ProjectModel): MediaTileInternals {
  return model as unknown as MediaTileInternals;
}

function storageContext(model: ProjectModel): {
  storage: IStorageModel;
  customerId: string;
  projectId: string;
} | null {
  return (
    model as unknown as {
      getStorageContext(): {
        storage: IStorageModel;
        customerId: string;
        projectId: string;
      } | null;
    }
  ).getStorageContext();
}

function publishMediaTileEvent(
  model: ProjectModel,
  type: string,
  sceneId: string,
  payload: Record<string, unknown>,
): void {
  (
    model as unknown as {
      publishLiveProjectEvent(type: string, payload: Record<string, unknown>): void;
    }
  ).publishLiveProjectEvent(type, {
    sceneId,
    SceneID: sceneId,
    SceneId: sceneId,
    ...payload,
  });
}

function insertTile(model: ProjectModel, sceneId: string, tile: MediaTileData): void {
  const state = internals(model);
  const list = state.m_mediaTilesByScene.get(sceneId) ?? [];
  state.m_mediaTilesByScene.set(sceneId, [...list, tile]);
  model.onMediaTilesChanged.emit({ sceneId });
}

function removeTile(model: ProjectModel, sceneId: string, tileId: string): void {
  const state = internals(model);
  const list = state.m_mediaTilesByScene.get(sceneId);
  if (!list) return;
  const next = list.filter((tile) => tile.id !== tileId);
  if (next.length === list.length) return;
  state.m_mediaTilesByScene.set(sceneId, next);
  model.onMediaTilesChanged.emit({ sceneId });
}

function replaceTile(model: ProjectModel, sceneId: string, tile: MediaTileData): void {
  const state = internals(model);
  const list = state.m_mediaTilesByScene.get(sceneId) ?? [];
  state.m_mediaTilesByScene.set(
    sceneId,
    list.map((existing) => (existing.id === tile.id ? tile : existing)),
  );
}

/**
 * Insert, persist, broadcast and select a new card — the shared tail of
 * Unity's `CreateMediaTile(scene, data, applyToStorage: true)`
 * (`ProjectModel_MediaTiles.cs:114-164`), used by "Add card" and Duplicate.
 * Rows are written in `ProjectDataModel.CreateMediaTileAsync`'s order: asset,
 * tile, then the scene's `MediaAssetIDs` list.
 */
async function createMediaTileAsync(
  model: ProjectModel,
  sceneId: string,
  tile: MediaTileData,
  signal?: AbortSignal,
): Promise<void> {
  const state = internals(model);
  state.m_mediaAssetsById.set(tile.asset.id, tile.asset);
  insertTile(model, sceneId, tile);

  const context = storageContext(model);
  if (context) {
    try {
      await context.storage.createMediaAsset(
        context.customerId,
        context.projectId,
        mediaAssetDataToDto(tile.asset),
        signal,
      );
      await context.storage.createMediaTile(
        context.customerId,
        context.projectId,
        sceneId,
        mediaTileDataToDto(tile),
        signal,
      );
      await appendSceneMediaAssetId(model, context, sceneId, tile.asset.id, signal);
    } catch (err) {
      removeTile(model, sceneId, tile.id);
      state.m_mediaAssetsById.delete(tile.asset.id);
      throw err;
    }
  }

  publishMediaTileEvent(model, "create_mediatile", sceneId, {
    mt: wireMediaTile(tile),
    MediaTileData: wireMediaTile(tile),
  });

  // Unity selects the created GameObject (`SelectObjects(new[]{go}, true)`).
  if (Ioc.isRegistered<ISelectionModel>(SELECTION_MODEL)) {
    Ioc.resolve<ISelectionModel>(SELECTION_MODEL).set([tile.id]);
  }
  // Local edit → refresh the scene card (debounced).
  model.scheduleActiveSceneThumbnail();
}

/**
 * Push an already-applied edit onto the undo stack. `record` runs `redo` once
 * on push, and that first run is skipped — the edit has already happened and
 * been persisted by the caller (`recordImportUndo`'s shape). Replays persist
 * and broadcast like any other edit; a failure is logged rather than thrown,
 * since nothing is awaiting an undo.
 */
function recordUndo(
  label: string,
  command: { readonly redo: () => Promise<void>; readonly undo: () => Promise<void> },
): void {
  if (!Ioc.isRegistered<IUndoRedo>(UNDO_REDO)) return;
  let firstRedo = true;
  Ioc.resolve<IUndoRedo>(UNDO_REDO).record({
    label,
    redo: () => {
      if (firstRedo) {
        firstRedo = false;
        return;
      }
      void command.redo().catch((err: unknown) => {
        console.warn(`[ProjectModel] redo "${label}" failed:`, err);
      });
    },
    undo: () => {
      void command.undo().catch((err: unknown) => {
        console.warn(`[ProjectModel] undo "${label}" failed:`, err);
      });
    },
  });
}

/** The card's current values for exactly the keys `patch` carries. */
function pickTilePatch(tile: MediaTileData, patch: MediaTilePatch): MediaTilePatch {
  const before: Record<string, unknown> = {};
  for (const key of Object.keys(patch) as Array<keyof MediaTilePatch>) {
    before[key] = tile[key];
  }
  return before as MediaTilePatch;
}

function patchTile(tile: MediaTileData, patch: MediaTilePatch): MediaTileData {
  return {
    ...tile,
    name: patch.name ?? tile.name,
    position: patch.position ?? tile.position,
    rotationEuler: patch.rotationEuler ?? tile.rotationEuler,
    scale: patch.scale ?? tile.scale,
    visibility: patch.visibility ?? tile.visibility,
    isLocked: patch.isLocked ?? tile.isLocked,
    forcedVisibility: patch.forcedVisibility ?? tile.forcedVisibility,
    billboard: patch.billboard ?? tile.billboard,
    width: patch.width ?? tile.width,
    fixedWidth: patch.fixedWidth ?? tile.fixedWidth,
    backgroundColor: patch.backgroundColor ?? tile.backgroundColor,
    imagePlacement: patch.imagePlacement ?? tile.imagePlacement,
  };
}

/**
 * A new card faces the viewer, stored as Unity Euler.
 *
 * The camera's rotation is a WORLD one (the camera hangs off `engine.scene`,
 * and in a session three decomposes the head pose onto it), while
 * `MediaTileData.rotationEuler` is SCENE-relative — `mediaTileRenderer` writes
 * it onto a group under `engine.root`, the stage. The two coincide until
 * console placement moves the stage, after which a card created in-headset
 * would be persisted facing the wrong way — permanently, since new cards ship
 * with billboard OFF. So convert through the stage first, then the basis map,
 * exactly as the multiuser publish and the sketch pose do (see
 * `runtime/xr/stageSpace.ts`).
 *
 * `Add card` IS reachable in a session: the XR Universal Menu hides only
 * Settings / Asset Library / Share / Help.
 */
function defaultMediaTileRotation(): Vec3 {
  if (!Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)) {
    return [0, 0, 0];
  }
  const camera = Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA).camera;
  const stageRoot = tryResolveStageRoot();
  if (!stageRoot) return threeCameraQuaternionToUnityEuler(camera.quaternion);
  return threeCameraQuaternionToUnityEuler(
    worldToStageQuaternion(stageRoot, camera.quaternion, new Quaternion()),
  );
}

/**
 * The stage root, or null before the viewport has initialized the engine —
 * `EngineService.root` THROWS in that state, and a card created headlessly
 * (an AI tool during a project load) must not fail over a facing.
 */
function tryResolveStageRoot(): Object3D | null {
  if (!Ioc.isRegistered<EngineService>(ENGINE_SERVICE)) return null;
  try {
    return Ioc.resolve<EngineService>(ENGINE_SERVICE).root;
  } catch {
    return null;
  }
}

async function appendSceneMediaAssetId(
  model: ProjectModel,
  context: { storage: IStorageModel; customerId: string; projectId: string },
  sceneId: string,
  mediaAssetId: string,
  signal?: AbortSignal,
): Promise<void> {
  const scene = model.getScene(sceneId);
  if (!scene) return;
  const next = [...new Set([...scene.mediaAssetIds, mediaAssetId])];
  model.applySceneChanges({ ...scene, mediaAssetIds: next });
  await context.storage.updateSceneMediaAssetIDs(
    context.customerId,
    context.projectId,
    sceneId,
    next,
    signal,
  );
}

async function removeSceneMediaAssetId(
  model: ProjectModel,
  context: { storage: IStorageModel; customerId: string; projectId: string },
  sceneId: string,
  mediaAssetId: string,
  signal?: AbortSignal,
): Promise<void> {
  const scene = model.getScene(sceneId);
  if (!scene || !scene.mediaAssetIds.includes(mediaAssetId)) return;
  const next = scene.mediaAssetIds.filter((id) => id !== mediaAssetId);
  model.applySceneChanges({ ...scene, mediaAssetIds: next });
  await context.storage.updateSceneMediaAssetIDs(
    context.customerId,
    context.projectId,
    sceneId,
    next,
    signal,
  );
}

function isImageInUse(model: ProjectModel, imageId: string): boolean {
  for (const asset of internals(model).m_mediaAssetsById.values()) {
    if (asset.imageId === imageId) return true;
  }
  return false;
}

function evictImageUrl(model: ProjectModel, imageId: string): void {
  if (!imageId) return;
  const state = internals(model);
  const url = state.m_mediaTileImageUrlById.get(imageId);
  if (!url) return;
  URL.revokeObjectURL(url);
  state.m_mediaTileImageUrlById.delete(imageId);
}

/**
 * Unity-JsonUtility-compatible `MediaTileData` wire shape (see
 * `mapWireMediaTile` for the inbound side).
 */
function wireMediaTile(tile: MediaTileData): Record<string, unknown> {
  return {
    id: tile.id,
    assetId: tile.assetId,
    name: tile.name,
    scale: tile.scale,
    visibility: tile.visibility,
    isLocked: tile.isLocked,
    forcedVisibility: tile.forcedVisibility,
    billboard: tile.billboard,
    width: tile.width,
    fixedWidth: tile.fixedWidth,
    bgColor: tile.backgroundColor,
    imagePlacement: tile.imagePlacement,
    pos: nameFloatPairs(tile.position),
    euler: nameFloatPairs(tile.rotationEuler),
  };
}

/**
 * `BulkMediaTileData` wire shape for `update_mediatile`
 * (`MediaTileEventData.cs:82` + `GenerateSharedModification`,
 * `ProjectModel_MediaTiles.cs:346`).
 */
function wireBulkData(
  tile: MediaTileData,
  flags: ReadonlyArray<MediaTileUpdateFlag>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (flags.includes("Transform")) {
    out.Position = { x: tile.position[0], y: tile.position[1], z: tile.position[2] };
    out.Rotation = {
      x: tile.rotationEuler[0],
      y: tile.rotationEuler[1],
      z: tile.rotationEuler[2],
    };
    out.Scale = { x: tile.scale, y: tile.scale, z: tile.scale };
  }
  if (flags.includes("Visibility")) {
    out.visibility = tile.visibility;
    out.isForcedVisibility = false;
  }
  if (flags.includes("Lock")) out.isLocked = tile.isLocked;
  if (flags.includes("DisplayName")) out.name = tile.name;
  if (flags.includes("MediaTile")) {
    out.width = tile.width;
    out.billboard = tile.billboard;
    out.bgColor = tile.backgroundColor;
    out.placement = tile.imagePlacement;
  }
  return out;
}

/** Inbound `BulkMediaTileData` application (Unity `ApplyMediaTileChanges`). */
function applyWireBulkData(
  tile: MediaTileData,
  wire: Record<string, unknown>,
  flagMask: number,
): MediaTileData {
  let next = tile;
  if (flagMask & (1 << 0)) {
    const position = vec3From(
      toNameFloatMap(wire.Position ?? wire.position ?? wire.pos),
      tile.position,
    );
    const rotation = vec3From(
      toNameFloatMap(wire.Rotation ?? wire.rotation ?? wire.euler),
      tile.rotationEuler,
    );
    const scaleMap = toNameFloatMap(wire.Scale ?? wire.scale);
    const scale = typeof scaleMap.x === "number" ? scaleMap.x : tile.scale;
    next = { ...next, position, rotationEuler: rotation, scale };
  }
  if (flagMask & (1 << 1)) {
    const visibility = boolOf(wire, "visibility", "Visibility");
    const isForced = boolOf(wire, "isForcedVisibility", "IsForcedVisibility") === true;
    if (visibility !== null) {
      next = isForced
        ? { ...next, forcedVisibility: visibility }
        : { ...next, visibility };
    }
  }
  if (flagMask & (1 << 2)) {
    const isLocked = boolOf(wire, "isLocked", "IsLocked");
    if (isLocked !== null) next = { ...next, isLocked };
  }
  if (flagMask & (1 << 5)) {
    const name = stringOf(wire, "name", "Name");
    if (name) next = { ...next, name };
  }
  if (flagMask & (1 << 10)) {
    const widthMap = toNameFloatMap({ w: wire.width ?? wire.Width });
    const billboardMap = toNameFloatMap({ b: wire.billboard ?? wire.Billboard });
    const bgColor = toNameFloatMap(wire.bgColor ?? wire.BgColor ?? wire.BackgroundColor);
    const placement = toNameFloatMap({ p: wire.placement ?? wire.imagePlacement ?? wire.ImagePlacement });
    next = {
      ...next,
      width: typeof widthMap.w === "number" ? Math.round(widthMap.w) : next.width,
      billboard: typeof billboardMap.b === "number" ? billboardMap.b : next.billboard,
      backgroundColor:
        typeof bgColor.r === "number"
          ? { r: bgColor.r, g: bgColor.g ?? 0, b: bgColor.b ?? 0, a: bgColor.a ?? 1 }
          : next.backgroundColor,
      imagePlacement:
        typeof placement.p === "number" && placement.p === 1
          ? MEDIA_TILE_PLACEMENT.Top
          : typeof placement.p === "number"
            ? MEDIA_TILE_PLACEMENT.Bottom
            : next.imagePlacement,
    };
  }
  // `UpdateEntityFlags.Highlight`. `BulkMediaTileData` inherits the snapshot
  // pair, so this reads exactly what `update_entitysnapshot` does
  // (`projectEventReconciliation.ts`). Nothing current sends it — the web
  // never publishes a highlight and Unity stopped on 2026-09-22 — but an older
  // desktop build still does, and its peers drew the card it named.
  if (flagMask & (1 << 15)) {
    if (boolOf(wire, "hasPersistentHighlightUpdate", "HasPersistentHighlightUpdate") === true) {
      next = {
        ...next,
        persistentHighlightJson: highlightJsonOrNull(
          wire.persistentHighlightJson ?? wire.PersistentHighlightJson,
        ),
      };
    }
  }
  return next;
}

function nameFloatPairs(v: Vec3): Array<{ Name: string; Val: number }> {
  return [
    { Name: "x", Val: v[0] },
    { Name: "y", Val: v[1] },
    { Name: "z", Val: v[2] },
  ];
}

function stringOf(
  wire: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): string | null {
  for (const key of keys) {
    const value = wire[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

function boolOf(
  wire: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): boolean | null {
  for (const key of keys) {
    const value = wire[key];
    if (typeof value === "boolean") return value;
  }
  return null;
}
